import * as React from 'react'

import { observer } from 'mobx-react'

import GLSharedPinLayer from '~/client/src/shared/components/LogisticsMapView/GLSharedPinLayer'
import GLWorkflowPinLayer, {
  DELIVERY_PINS_LAYER_ID,
} from '~/client/src/shared/components/LogisticsMapView/GLWorkflowPinLayer'
import { IWorkflowPinPoint } from '~/client/src/shared/components/LogisticsMapView/workflowPinPoints'
import MapBoxViewerStore from '~/client/src/shared/components/MapBoxViewer/MapBoxViewer.store'
import GlobeViewFiles from '~/client/src/shared/components/SitemapHelpers/components/GlobeViewFiles/GlobeViewFiles'
import GlobeViewItems from '~/client/src/shared/components/SitemapHelpers/components/GlobeViewItems'
import MapViewItemBase from '~/client/src/shared/components/SitemapHelpers/models/MapViewItemBase'

interface IProps {
  mapBoxViewerStore: MapBoxViewerStore
  deliveryPoints: IWorkflowPinPoint[]
  sharedPoints: IWorkflowPinPoint[]
  onDeliveryPinClick: (coordKey: string) => void
  onSharedPinClick: (coordKey: string) => void
  // Desktop only: there the items layer is selectable and narrowed to the view's date window.
  // Mobile drives selection through the pins instead, so it passes none of these.
  onSelectItem?: (item: MapViewItemBase) => void
  selectedStartDate?: Date
  selectedEndDate?: Date
  // Popups, which only the desktop map mounts inside the map tree.
  children?: React.ReactNode
}

/**
 * The GL layer stack every delivery map draws: the globe's map files, its location items, the
 * delivery pins and the pins of deliveries shared in from another project.
 *
 * An observer, because the layer props it derives — the zoom-scaling flag, the globe's map files —
 * are read here rather than by the host.
 */
const DeliveryMapLayers: React.FC<IProps> = observer(
  ({
    mapBoxViewerStore,
    deliveryPoints,
    sharedPoints,
    onDeliveryPinClick,
    onSharedPinClick,
    onSelectItem,
    selectedStartDate,
    selectedEndDate,
    children,
  }) => {
    const { isZoomScalingEnabled } = mapBoxViewerStore.globeViewStyle

    return (
      <>
        <GlobeViewFiles
          mapBoxViewerStore={mapBoxViewerStore}
          sitemapWithBasemaps={mapBoxViewerStore.sitemapWithBasemapsOnGlobe}
          mapFilesOnGlobe={mapBoxViewerStore.mapFilesOnGlobe}
        />
        <GlobeViewItems
          items={mapBoxViewerStore.displayedGlobeViewItems || []}
          mapBoxViewerStore={mapBoxViewerStore}
          onSelectItem={onSelectItem}
          selectedStartDate={selectedStartDate}
          selectedEndDate={selectedEndDate}
        />
        <GLWorkflowPinLayer
          layerId={DELIVERY_PINS_LAYER_ID}
          points={deliveryPoints}
          isZoomScalingEnabled={isZoomScalingEnabled}
          onPinClick={onDeliveryPinClick}
          onHoverChange={mapBoxViewerStore.setOverlayPointerHovered}
        />
        <GLSharedPinLayer
          points={sharedPoints}
          isZoomScalingEnabled={isZoomScalingEnabled}
          onPinClick={onSharedPinClick}
          onHoverChange={mapBoxViewerStore.setOverlayPointerHovered}
        />
        {children}
      </>
    )
  },
)

export default DeliveryMapLayers
